/**
 * 1.8.5 (D-1.8.5-6, D-1.8.5-10) — the Tech Wizard: what the app and the API share.
 *
 *   - redactContacts(): phone numbers and email addresses typed inside a ticket's description (or a question) are
 *     removed before anything is sent to the model (Elijah's word D-1.8.5-0b);
 *   - the monthly cap's tiers (under $10 normal, $10–$15 first model only, at $15 paused) and the settings that hold
 *     the prices and the two thresholds (Admin → Settings → Tech Support — never in code; these are only the
 *     defaults a site with no row uses);
 *   - the steps a session walks: candidatesFor() / nextCandidate() — from a guided flow, or from the library entries a
 *     question found — never a step already tried, the safe ones first, anything that erases last with its warning;
 *     when nothing is left, trackEnd() gives the maker's support page and free line;
 *   - the shapes the API answers with.
 *
 * Pure: no Node APIs. Nothing here calls a model.
 */
import type { HelpKey } from './tech';
import type { GuidePlan } from './tech-plan';
import {
  ERASE_MARK,
  entrySteps,
  freeHelpFor,
  makersIn,
  nextStep,
  type FreeHelpLine,
  type FreeLine,
  type LibraryEntry,
  type Platform,
  type TechLibrary,
} from './tech-library';

/** Who may use the Wizard: the tech (his own tickets), the administrator, the co-administrator. */
export const WIZARD_ROLES = ['admin', 'coadmin', 'tech'] as const;

/* ---------------- the settings (Admin → Settings → Tech Support) ---------------- */

/** "Tech Wizard reads new tickets" (on): with it off the heads-up never runs. */
export const WIZARD_READS_TICKETS_KEY = 'TECH_WIZARD_READS_TICKETS';
/** Prices per 1 million tokens, per model (reasoning tokens are billed inside the output count). */
export const TECH_AI_LUNA_INPUT_KEY = 'TECH_AI_LUNA_INPUT_PER_M';
export const TECH_AI_LUNA_OUTPUT_KEY = 'TECH_AI_LUNA_OUTPUT_PER_M';
export const TECH_AI_TERRA_INPUT_KEY = 'TECH_AI_TERRA_INPUT_PER_M';
export const TECH_AI_TERRA_OUTPUT_KEY = 'TECH_AI_TERRA_OUTPUT_PER_M';
/** From this month's spend the first model answers everything ($10). */
export const TECH_AI_FIRST_ONLY_KEY = 'TECH_AI_FIRST_ONLY_AT';
/** At this month's spend the AI pauses until the 1st ($15, Elijah's limit). */
export const TECH_AI_PAUSE_KEY = 'TECH_AI_PAUSE_AT';

/**
 * The defaults a site with no row uses. The prices are the catalog's (sources.md §15, Global Standard, 10 Oct 2026)
 * plus the 10% Data Zone US costs — the resource is Data Zone US, and a cap should round up, not down.
 */
export const TECH_AI_DEFAULTS = {
  [WIZARD_READS_TICKETS_KEY]: 'Yes',
  [TECH_AI_LUNA_INPUT_KEY]: '0.22',
  [TECH_AI_LUNA_OUTPUT_KEY]: '1.32',
  [TECH_AI_TERRA_INPUT_KEY]: '2.20',
  [TECH_AI_TERRA_OUTPUT_KEY]: '13.20',
  [TECH_AI_FIRST_ONLY_KEY]: '10',
  [TECH_AI_PAUSE_KEY]: '15',
} as const;

export interface TechAiPrices {
  luna: { input: number; output: number };
  terra: { input: number; output: number };
  firstOnlyAt: number;
  pauseAt: number;
}

/** The worst case a call may cost: the most it may answer with (Luna 3,000 tokens, Terra 5,000). */
export const MAX_OUTPUT_TOKENS = { first: 3000, strong: 5000 } as const;

export type AiTier = 'normal' | 'first-only' | 'paused';

/** Under the first threshold: normal. From it: the first model only. At the ceiling: paused for the month. */
export function tierFor(spentUsd: number, firstOnlyAt: number, pauseAt: number): AiTier {
  if (spentUsd >= pauseAt) return 'paused';
  if (spentUsd >= firstOnlyAt) return 'first-only';
  return 'normal';
}

/** The month the ledger counts, as yyyyMM, from a New York civil date key (yyyyMMdd). */
export const monthOfKey = (dayKey: string): string => dayKey.slice(0, 6);

/* ---------------- the redactor ---------------- */

/** Email addresses as written, and as people write them to dodge the @ ("name (at) example (dot) com"). */
const EMAIL_RES: RegExp[] = [
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g,
  /[A-Za-z0-9._%+-]+\s*[([]\s*at\s*[)\]]\s*[A-Za-z0-9-]+(?:\s*(?:[([]\s*dot\s*[)\]]|\.)\s*[A-Za-z0-9-]+)+/gi,
];
/**
 * Phone numbers as people type them: (212) 555-0100, 212-555-0100, 212.555.0100, 212 555 0100, +1 212 555 0100,
 * 1-800-275-2273, 2125550100, 555-0100, and international +44 20 7946 0958 — also at the end of a sentence. Model
 * numbers (G3100, CR1000A), versions (iOS 27.0.1, 22H2) and serial numbers without the phone shape are left alone.
 */
const END = String.raw`(?![\w-]|\.\d)`;
const PHONE_RES: RegExp[] = [
  /\+\d{1,3}(?:[\s.-]?\(?\d{1,4}\)?){2,5}\d/g,
  new RegExp(String.raw`(?<![\w.])(?:1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]\d{4}${END}`, 'g'),
  new RegExp(String.raw`(?<![\w.])(?:1[\s.-]?)?\(\d{3}\)\s?\d{7}${END}`, 'g'),
  new RegExp(String.raw`(?<![\w.-])1?\d{10}${END}`, 'g'),
  new RegExp(String.raw`(?<![\w.-])\d{3}[\s.-]\d{4}${END}`, 'g'),
];

export const REDACTED_PHONE = '[phone removed]';
export const REDACTED_EMAIL = '[email removed]';

/** Phone numbers and email addresses out of a text that may go to the model. */
export function redactContacts(text: string): string {
  let s = String(text ?? '');
  for (const re of EMAIL_RES) s = s.replace(re, REDACTED_EMAIL);
  for (const re of PHONE_RES) s = s.replace(re, REDACTED_PHONE);
  return s;
}

/* ---------------- steps, sessions and their end ---------------- */

export type Sure = 'high' | 'medium' | 'low';

export interface WizardSource {
  title: string;
  url: string;
  /** The library entry it comes from. */
  entryId: string;
  maker: string;
}

export interface WizardStep {
  /** `flow:<flowId>:<stepId>` or `<entryId>#<n>` (n from 1) — what "I already tried that" records. */
  key: string;
  text: string;
  /** True when the step erases settings or data: it comes last, with its warning. */
  erases: boolean;
  /** "This erases saved Wi-Fi passwords" — always present when `erases`. */
  warning?: string;
  /** "Did that work?" in other words, when the flow gives them. */
  check?: string;
  /** The library entry it comes from. */
  entry: string;
  sourceUrl?: string;
  /** The first supporting quote, exactly as the manual writes it (kept for older readers). */
  quote?: string;
  /** D-1025-WIZ-2: up to three supporting quotes, each as the manual writes it, with the section it was found in. */
  quotes?: WizardQuote[];
  /** Other documented ways to do the same thing; each passed the same check (NVDA+N with Insert or Caps Lock). */
  alternatives?: WizardAlternative[];
  /** D-1025-WIZ-3: a step of a fixed walk-through the office wrote, with no maker page behind it: "Our office procedure". */
  office?: boolean;
}

/** A supporting quote: the manual's own words and where they were found. */
export interface WizardQuote {
  sectionId: string;
  quote: string;
  /** The section's page, when known. */
  url?: string;
}

export interface WizardAlternative {
  text: string;
  quotes: WizardQuote[];
  sourceUrl?: string;
  entry?: string;
}

/** Why a proposed step was not confirmed in the manual (D-1025-WIZ-2). `detail` carries the missing key or label. */
export type UnconfirmedReason = 'quote-not-found' | 'key-not-in-quote' | 'quote-too-short' | 'no-quote' | 'erases';

/** A proposed step the check could not confirm. Shown muted under "Not confirmed in the manual (n)"; never walked, never summarised. */
export interface UnconfirmedStep {
  text: string;
  reason: UnconfirmedReason;
  detail?: string;
  /** Set when this is an alternative of step n (1-based) that did not pass. */
  alternativeOf?: number;
}

/** D-1025-WIZ-2: the answer's label. `supported` only when every step was found in the manual and nothing is missing. */
/** `unconfirmed` (D-1101-TECHANSWER-3): the classic fallback answered because the checked engine found no evidence; nothing in it is confirmed. */
export type AnswerLabel = 'supported' | 'partly' | 'none' | 'unconfirmed';

/** How an answer was made: the engine and where the time went (D-1025-WIZ-4). Numbers only, never the question or the answer. */
export interface AnswerTrace {
  engine: WizardEngine;
  /** D-1101-TECHANSWER-3: the checked engine found nothing it could confirm and the classic ladder answered instead. */
  fallback?: boolean;
  totalMs: number;
  /** The keyword search. */
  searchMs?: number;
  /** The meaning (embedding) search; 0 when it fell back to keywords. */
  meaningMs?: number;
  aiMs?: number;
  checkMs?: number;
  tokensIn?: number;
  tokensOut?: number;
  /** Sections sent to the model / left out for size. */
  sections?: number;
  dropped?: number;
  /** Steps confirmed / not confirmed. */
  checked?: number;
  unconfirmed?: number;
}

export const WIZARD_ENGINES = ['checked', 'classic'] as const;
export type WizardEngine = (typeof WIZARD_ENGINES)[number];
/** The admin setting "Tech Wizard engine" (D-1025-WIZ-1): Checked (new) or Classic (before 25 Oct). Default Checked. */
export const WIZARD_ENGINE_KEY = 'TECH_WIZARD_ENGINE';
export const WIZARD_ENGINE_DEFAULT: WizardEngine = 'checked';
export const wizardEngineOf = (value: unknown): WizardEngine => (/^classic/i.test(String(value ?? '').trim()) ? 'classic' : 'checked');

/** What a session works from: one guided flow, or the entries a question found (best first). */
export interface WizardTrack {
  flowId?: string;
  entryIds: string[];
  help?: HelpKey;
  device?: string;
}

export interface WizardEnd {
  maker: Platform | null;
  supportUrl: string | null;
  freeLine: FreeLine | null;
}

/** An entry's steps as session steps (the erase mark becomes the warning). */
export function entryCandidates(entry: LibraryEntry): WizardStep[] {
  return entrySteps(entry.body).map((raw, i) => {
    const erases = raw.startsWith(ERASE_MARK);
    if (!erases) return { key: `${entry.id}#${i + 1}`, text: raw, erases: false, entry: entry.id };
    // "⚠ This erases everything on the phone: factory reset only with…" → warning + text
    const body = raw.slice(2).trim();
    const colon = body.indexOf(':');
    const warning = (colon > 0 ? body.slice(0, colon) : body).trim();
    const text = colon > 0 ? body.slice(colon + 1).trim() : body;
    return { key: `${entry.id}#${i + 1}`, text: text ? text.charAt(0).toUpperCase() + text.slice(1) : warning, erases: true, warning: warning.endsWith('.') ? warning : `${warning}.`, entry: entry.id };
  });
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2);

/** Two steps that say the same thing in other words (most of the shorter one's words are in the longer). */
export function sameStep(a: string, b: string): boolean {
  const x = new Set(norm(a));
  const y = new Set(norm(b));
  if (!x.size || !y.size) return a.trim().toLowerCase() === b.trim().toLowerCase();
  const [small, big] = x.size <= y.size ? [x, y] : [y, x];
  let hit = 0;
  for (const w of small) if (big.has(w)) hit++;
  return hit / small.size >= 0.8;
}

/**
 * Every step a track can offer, in the order the Wizard offers them: a flow's own order (its safe steps, then the
 * ones that erase); for entries, the safe steps of every entry in rank order, then the erasing ones. No two steps
 * say the same thing.
 */
export function candidatesFor(lib: Pick<TechLibrary, 'entries' | 'flows'>, track: WizardTrack): WizardStep[] {
  if (track.flowId) {
    const flow = lib.flows.find((f) => f.id === track.flowId);
    if (flow) {
      const ordered = [...flow.steps.filter((s) => !s.erases), ...flow.steps.filter((s) => s.erases)];
      return ordered.map((s) => ({ key: `flow:${flow.id}:${s.id}`, text: s.text, erases: s.erases, ...(s.warning ? { warning: s.warning } : {}), ...(s.check ? { check: s.check } : {}), entry: s.entry, ...(s.sourceUrl ? { sourceUrl: s.sourceUrl, quote: s.quote } : {}) }));
    }
  }
  const all = track.entryIds.map((id) => lib.entries.find((e) => e.id === id)).filter((e): e is LibraryEntry => !!e).flatMap(entryCandidates);
  const out: WizardStep[] = [];
  for (const s of [...all.filter((x) => !x.erases), ...all.filter((x) => x.erases)]) if (!out.some((o) => sameStep(o.text, s.text))) out.push(s);
  return out;
}

/**
 * "I already tried that": the steps not tried yet, in order — never a key in `tried`, never a step that says what a
 * tried step said. The first of them is the next most likely fix (the model may pick another of the safe ones).
 */
export function remainingSteps(lib: Pick<TechLibrary, 'entries' | 'flows'>, track: WizardTrack, tried: readonly string[], triedTexts: readonly string[] = []): WizardStep[] {
  const done = new Set(tried);
  if (track.flowId) {
    const flow = lib.flows.find((f) => f.id === track.flowId);
    // the flow's own rule (shared/tech-library.ts nextStep) decides whether anything is left
    if (flow) {
      const ids = flow.steps.map((s) => s.id).filter((id) => done.has(`flow:${flow.id}:${id}`));
      if (nextStep(flow, ids).done) return [];
    }
  }
  return candidatesFor(lib, track).filter((s) => !done.has(s.key) && !triedTexts.some((t) => sameStep(t, s.text)));
}

export function nextCandidate(lib: Pick<TechLibrary, 'entries' | 'flows'>, track: WizardTrack, tried: readonly string[], triedTexts: readonly string[] = []): WizardStep | null {
  return remainingSteps(lib, track, tried, triedTexts)[0] ?? null;
}

/** The maker's line from the free-help table: the line for this maker and help kind, or any line of the maker's. */
function makerLine(lib: Pick<TechLibrary, 'freeHelp'>, maker: Platform, help?: HelpKey): FreeLine | null {
  const lines = lib.freeHelp.lines.filter((l) => l.maker === maker && l.phones.length > 0 && !l.scam);
  const best = (help ? lines.find((l) => l.helps === 'all' || l.helps.includes(help)) : undefined) ?? lines[0];
  return best ? { name: best.who, phone: best.phones[0]!, hours: best.hours ?? '' } : null;
}

/** Where a track ends when nothing is left: the maker's support page and the maker's free line, from the library. */
export function trackEnd(lib: Pick<TechLibrary, 'entries' | 'flows' | 'freeHelp'>, track: WizardTrack): WizardEnd {
  const flow = track.flowId ? lib.flows.find((f) => f.id === track.flowId) : undefined;
  if (flow) return { maker: flow.end.maker, supportUrl: flow.end.supportUrl, freeLine: flow.end.freeLine ?? makerLine(lib, flow.end.maker, track.help) };
  const entry = track.entryIds.map((id) => lib.entries.find((e) => e.id === id)).find((e): e is LibraryEntry => !!e);
  const maker = entry?.platform ?? makersIn(`${track.device ?? ''}`)[0] ?? null;
  return { maker, supportUrl: entry?.sources[0]?.url ?? null, freeLine: maker ? makerLine(lib, maker, track.help) : null };
}

/** The maker's links of the entries a step list came from — the Sources, never anything else. */
export function sourcesOf(lib: Pick<TechLibrary, 'entries'>, entryIds: readonly string[]): WizardSource[] {
  const out: WizardSource[] = [];
  for (const id of entryIds) {
    const e = lib.entries.find((x) => x.id === id);
    if (!e) continue;
    for (const s of e.sources) if (!out.some((o) => o.url === s.url)) out.push({ title: s.title, url: s.url, entryId: e.id, maker: e.platform });
  }
  return out;
}

/** "Is there free help for this?" — the lines for this help kind, the maker's own first when the device names one. */
export function freeHelpLines(lib: Pick<TechLibrary, 'freeHelp'>, help: HelpKey, device = ''): FreeHelpLine[] {
  const maker = makersIn(device)[0];
  return freeHelpFor(lib, help, maker ? { maker } : {});
}

/* ---------------- what the API answers with ---------------- */

/** Where the AI stands this month (Health and the Wizard's banner). */
export type AiState = 'normal' | 'first-only' | 'paused' | 'not-configured' | 'offline';

export interface AiStatusView {
  state: AiState;
  month: string;
  spentUsd: number;
  firstOnlyAt: number;
  pauseAt: number;
}

/** How an answer was made. */
export interface AiUse {
  /** True when a model wrote it; false when it is the library's own steps (offline, paused, not set up, or a fallback). */
  used: boolean;
  tier: 'first' | 'strong' | null;
  /** A strong request answered by the first model (the month is past the first threshold). */
  downgraded: boolean;
  paused: boolean;
  /** The model stopped before it finished (still billed): what there is, said to be incomplete. */
  incomplete: boolean;
  offline: boolean;
}

export interface WizardAnswer {
  kind: 'answer';
  steps: WizardStep[];
  sources: WizardSource[];
  sure: Sure;
  /** False: "This is not from the maker's guides." */
  fromGuides: boolean;
  /** The model had nothing it was sure of. */
  unknown: boolean;
  ai: AiUse;
  coverage?: 'yes' | 'partly' | 'no';
  rung?: import('./manuals').AnswerRung;
  provenance?: string;
  learnedId?: string;
  related?: WizardSource[];
  clarification?: string[];
  missing?: string[];
  knowledgeAvailable?: boolean;
  topics?: WizardSource[];
  observed?: { key: string; value: number; unit: 'Mbps' | 'ms' }[];
  /** D-1025-WIZ-2 (checked engine): steps the check could not confirm in the manual. */
  unconfirmed?: UnconfirmedStep[];
  /** D-1025-WIZ-2 (checked engine only; the classic engine's steps are not checked, so it has no label). */
  label?: AnswerLabel;
  /** D-1101-TECHANSWER-2: the steps as a plan to walk one at a time: prerequisites, steps with what you will hear, a verify step and a backup route. */
  plan?: GuidePlan;
  /** D-1025-WIZ-3: set when a fixed walk-through answered. */
  guide?: { id: string; title: string; reviewedBy: string | null; reviewedAt: string | null };
  /** D-1025-WIZ-1/4: the engine and the time each part took. */
  trace?: AnswerTrace;
}

export type WalkAsk = 'happening' | 'device' | 'does';

/** What the Tech Helper understood from the words so far ("NVDA screen reader · Windows computer"); the tech may Change it. */
export interface Understood {
  help: HelpKey | null;
  device: string;
  software: string | null;
  /** "NVDA screen reader · Windows computer". */
  label: string;
}

/** One likely answer to offer as a choice. A device choice carries the help kind and device it sets. */
export interface WalkChoice {
  label: string;
  help?: HelpKey;
  device?: string;
}

export type WalkTurn =
  | { kind: 'question'; ask: WalkAsk; ai: AiUse; /** the question in the AI's words, when it chose one */ prompt?: string; choices?: WalkChoice[]; understood?: Understood }
  | { kind: 'step'; step: WizardStep; remaining: number; title: string | null; sources: WizardSource[]; ai: AiUse; understood?: Understood }
  | { kind: 'guide'; answer: WizardAnswer; ai: AiUse; understood?: Understood }
  | { kind: 'end'; fixed: boolean; end: WizardEnd; followed: FollowedStep[]; nothingLeft: boolean; ai: AiUse };

export interface FollowedStep {
  key: string;
  text: string;
  outcome: 'fixed' | 'notFixed' | 'else' | 'tried';
  /** "Something else": what happened, as the tech typed it. */
  note?: string;
}

export interface HeadsUpView {
  status: 'ready' | 'pending' | 'off' | 'none';
  causes: string[];
  checkFirst: WizardStep[];
  sources: WizardSource[];
  sure: Sure;
  fromGuides: boolean;
  at: string | null;
  ai: AiUse | null;
}

export interface WizardStateView {
  ai: AiStatusView;
  /** On a ticket: its help kind and device (for the free-help answer). */
  help: HelpKey | null;
  device: string;
  followed: FollowedStep[];
  triedCount: number;
}

export interface FreeHelpView {
  help: HelpKey;
  maker: Platform | null;
  lines: FreeHelpLine[];
}

export interface LibraryChecksView {
  today: string;
  checks: Record<string, { by: string; at: string }>;
}
